// Builds the grounded context object sent to the "Ask about this deal" assistant, and a
// small set of deterministic intent matchers for the handful of scenarios the page
// already computes exactly (exit year, cash until handover, ROI at maturity, break-even,
// monthly net income). Deterministic matches never touch the LLM — they read the exact
// number straight off the engine's output, so they can never drift from what the report
// shows. Anything that doesn't match falls through to the AI backend (see
// components/AnalysisAssistant.jsx), which still gets this same context object so it's
// grounded in real figures rather than guessing.
import { computeCashFlow } from './analysisEngine';

const r = (n) => Math.round(Number(n) || 0);
const p1 = (n) => Number((Number(n) || 0).toFixed(1));
const dateStr = (d) => {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d);
  return isNaN(x) ? null : x.toISOString().slice(0, 10);
};

function scheduleForContext(a) {
  return (a.schedule || []).map(row => ({
    label: row.label,
    date: dateStr(row.date),
    amount_aed: r(row.amount),
    cumulative_aed: r(row.cumAED),
    cumulative_pct: row.cumPct != null ? p1(row.cumPct) : null,
    is_fee: !!row.isFee,
    is_handover_milestone: !!row.isHandover,
    is_post_handover_phpp: !!row.isPost,
  }));
}

function projectionForContext(a) {
  const isStr = a.strategy === 'str';
  return (a.years || []).map(y => ({
    calendar_year: y.year,
    years_since_handover: y.ysh,
    phase: y.phase,
    property_value_aed: r(y.value),
    annual_income_aed: r(isStr ? y.strGross : y.rent),
    service_charge_aed: r(y.sc),
    net_income_aed: r(y.netRent),
    cumulative_net_income_aed: r(y.cumNetRent),
    cash_deployed_to_date_aed: r(y.cashDeployed),
    appreciation_gain_aed: r(y.apprGain),
    combined_roi_pct: p1(y.combinedROI),
  }));
}

function exitScenariosForContext(a) {
  return (a.exitScenarios || []).map(s => ({
    years_after_handover: s.n,
    exit_date: dateStr(s.exitDate),
    sale_value_aed: r(s.saleValue),
    cash_invested_to_date_aed: r(s.cashInvested),
    outstanding_to_developer_aed: r(s.outstanding),
    net_rent_collected_aed: r(s.netRent),
    profit_aed: r(s.profit),
    net_after_fees_and_sc_aed: r(s.netAfter),
    roi_on_cash_pct: p1(s.roi),
    annualized_roi_pct: p1(s.annualizedRoi),
  }));
}

function cashFlowForContext(a) {
  const cf = computeCashFlow(a);
  if (!cf) return null;
  return {
    peak_cash_requirement_aed: r(cf.peak.magnitude),
    peak_cash_date: cf.peak.label,
    cash_break_even_reached: cf.breakEvenReached,
    cash_break_even_date: cf.breakEvenReached ? cf.breakEven.label : null,
    yearly: cf.yearly.map(row => ({
      year: row.year, payments_out_aed: r(row.payments), ownership_costs_out_aed: r(row.ownership),
      income_in_aed: r(row.rentIn), net_aed: r(row.net), cumulative_position_aed: r(row.cumulative),
    })),
  };
}

function strForContext(s) {
  if (!s || !s.available) return null;
  return {
    adr_aed_per_night: r(s.adr), occupancy_pct: s.occupancy,
    gross_revenue_yr_aed: r(s.gross), total_expenses_yr_aed: r(s.totalExpenses),
    net_income_yr_aed: r(s.net), net_yield_pct: p1(s.netYield),
    furnishing_cost_aed: r(s.furnishingCost),
    ltr_net_income_yr_aed: r(s.ltrNet), extra_vs_ltr_yr_aed: r(s.extraVsLtr),
    breakeven_occupancy_pct: s.breakevenOcc != null ? p1(s.breakevenOcc) : null,
    expense_line_items: (s.lineItems || []).map(li => ({ item: li.label, yearly_aed: r(li.yearly) })),
  };
}

// One analysis object -> its own context block (used for LTR and, when active, STR).
function blockForAnalysis(a) {
  if (!a) return null;
  return {
    strategy: a.strategy,
    dld: { enabled: a.dldAmount > 0, rate_pct: a.dldRate, amount_aed: r(a.dldAmount) },
    admin_fee_aed: r(a.admin),
    service_charge_yearly_aed: r(a.scYearly),
    expected_rent_yearly_aed: r(a.startRent),
    total_cost_incl_fees_aed: r(a.totalCost),
    cash_paid_until_handover_aed: r(a.paidUntilHandover),
    outstanding_after_handover_aed: r(a.outstandingBalance),
    maturity_years: a.maturityYears,
    yields_pct: { gross: p1(a.grossYield), net: p1(a.netYield), cash_on_cash_at_handover: p1(a.cashYield) },
    roi_pct: {
      at_handover_hold: p1(a.roiAt(a.atHandover)), flip_at_handover: p1(a.flipROI),
      at_maturity_hold: p1(a.roiAt(a.atMaturity)), combined_annualized_at_maturity: p1(a.combinedMaturityAnnual),
    },
    break_even_year: a.breakEven ? a.breakEven.year : null,
    payment_schedule: scheduleForContext(a),
    yearly_projection: projectionForContext(a),
    exit_scenarios: exitScenariosForContext(a),
    cash_flow: cashFlowForContext(a),
    str: strForContext(a.str),
  };
}

// Builds the full grounded context. `strategy` is whichever the page is currently
// showing ('ltr' | 'str' | 'compare'); both analysis (LTR) and analysisStr (STR) are
// included when available so the assistant can answer either regardless of the toggle.
export function buildAnalysisContext({ project, unit, analysis, analysisStr, strategy, capBands, rentBands, ils, rateMode }) {
  if (!project || !unit || !analysis) return null;
  return {
    deal: {
      developer: project.developer_name || null, project_name: project.project_name,
      area: project.area || null, emirate: project.emirate === 'rak' ? 'Ras Al Khaimah' : 'Dubai',
      unit_type: unit.unit_type, unit_number: unit.unit_number || null,
      internal_sqft: unit.internal_sqft, balcony_sqft: unit.balcony_sqft,
      price_aed: r(unit.price_aed),
      handover_date: dateStr(project.handover_date),
      is_post_handover_payment_plan: !!project.is_phpp,
    },
    fx: { mode: rateMode, ils_per_aed: Number((Number(ils) || 0).toFixed(4)) },
    strategy_currently_shown_on_page: strategy,
    projection_assumptions: {
      capital_appreciation_bands_pct_per_yr: { yrs_1_3: capBands?.[0], yrs_4_6: capBands?.[1], yrs_7_10: capBands?.[2] },
      rent_appreciation_bands_pct_per_yr: { yrs_1_3: rentBands?.[0], yrs_4_6: rentBands?.[1], yrs_7_10: rentBands?.[2] },
    },
    ltr: blockForAnalysis(analysis),
    str: analysisStr ? blockForAnalysis(analysisStr) : null,
    note: "Model assumes a cash purchase — no mortgage/financing is included anywhere in these figures.",
  };
}

const fmtAed = (v) => 'AED ' + Math.round(v).toLocaleString('en-GB');
const fmtIls = (v, ils) => '₪' + Math.round(v * ils).toLocaleString('en-GB');
const fmtBoth = (v, ils) => `${fmtAed(v)} (${fmtIls(v, ils)})`;
const monthShort = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '—');

// Pulls a target exit year (0..maxN) out of free text, if the question references one —
// used both by the deterministic matcher and to keep the Exit Scenario slider in sync
// even when the question ends up going to the LLM.
export function extractExitYear(question, maxN) {
  const q = (question || '').toLowerCase();
  if (maxN == null || maxN < 0) return null;
  const clamp = (n) => Math.max(0, Math.min(maxN, n));
  if (/\bhandover\b/.test(q) && /(sell|flip|exit)/.test(q)) return 0;
  if (/\bmatur(e|ity)\b/.test(q) && /(sell|exit)/.test(q)) return clamp(maxN);
  const m = q.match(/(\d+)\s*[- ]?\s*(years?|yrs?)\b/);
  if (m && /(sell|exit|flip|profit|hold|holding)/.test(q)) return clamp(parseInt(m[1], 10));
  return null;
}

// Deterministic answers for the handful of scenarios the engine already computes exactly
// — no LLM involved, so the number can never drift from what the card shows. Returns
// null when the question doesn't match a known pattern (caller falls through to the AI).
export function matchDeterministicIntent(question, { analysis, analysisStr, strategy, ils }) {
  const q = (question || '').toLowerCase();
  const wantsStr = /\b(str|airbnb|short[- ]?term)\b/.test(q);
  const wantsLtr = /\b(ltr|long[- ]?term)\b/.test(q) && !wantsStr;
  const a = wantsStr ? analysisStr : wantsLtr ? analysis : ((strategy === 'str' && analysisStr) ? analysisStr : analysis);
  if (!a) return null;
  const rate = Number(ils) || 0;
  const scenarios = a.exitScenarios || [];

  const exitYear = extractExitYear(question, scenarios.length - 1);
  if (exitYear != null && scenarios.length) {
    const s = scenarios[exitYear];
    const holdText = exitYear === 0 ? 'at handover' : `after ${exitYear} year${exitYear === 1 ? '' : 's'} of holding (from handover)`;
    const answer = `If you sell ${holdText} — ${monthShort(s.exitDate)} — the net profit is ${fmtBoth(s.profit, rate)}: `
      + `sale value ${fmtBoth(s.saleValue, rate)}, plus ${fmtBoth(s.netRent, rate)} net rent collected, less the purchase price and any outstanding balance. `
      + `ROI on cash invested: ${s.roi.toFixed(1)}% (${s.annualizedRoi.toFixed(1)}% annualized). Cash invested by then: ${fmtBoth(s.cashInvested, rate)}.`;
    return { answer, exitYear, basedOn: 'exit_scenario' };
  }

  if (/(cash|capital|money)\b.*\b(until|to|by)\s+handover|handover\b.*\b(cash|capital|money)/.test(q)) {
    const answer = `Total cash required until handover: ${fmtBoth(a.paidUntilHandover, rate)} — every milestone plus DLD/admin fees due before or at handover.`;
    return { answer, exitYear: null, basedOn: 'schedule' };
  }

  if (/\broi\b/.test(q) && /(maturity|long[- ]?term|final|end of|horizon)/.test(q)) {
    const answer = `Capital ROI at maturity (+${a.maturityYears}y, on cash deployed by then): ${a.roiAt(a.atMaturity).toFixed(1)}%. `
      + `Combined ROI (capital gain + net rent), annualized: ${a.combinedMaturityAnnual.toFixed(1)}%/yr.`;
    return { answer, exitYear: null, basedOn: 'roi' };
  }

  if (/break[- ]?even/.test(q)) {
    const answer = a.breakEven
      ? `Break-even year: ${a.breakEven.year} — that's when cumulative appreciation + net rent first covers the total fees paid.`
      : `Break-even isn't reached within the ${a.maturityYears}-year horizon shown.`;
    return { answer, exitYear: null, basedOn: 'break_even' };
  }

  if (/\bmonth(ly)?\b/.test(q) && /(rent|income|cash ?flow)/.test(q) && !/\b(sell|exit|flip)\b/.test(q)) {
    const monthly = a.annualNetRent / 12;
    const answer = `Projected monthly net ${a.strategy === 'str' ? 'STR income' : 'rent'} from handover: ${fmtBoth(monthly, rate)}/month. `
      + `This model assumes a cash purchase — no mortgage/financing is included, so there is no separate "mortgage-free" comparison to make.`;
    return { answer, exitYear: null, basedOn: 'net_income' };
  }

  return null;
}
